// src/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/detailedRentersTenantData.js

import { getRentersTenantsDashboardData } from "./rentersTenantsDashboardData.js";

/**
 * Get current language from localStorage
 */
function getCurrentLanguage() {
  return localStorage.getItem("lang") || "en";
}

/**
 * Format day display based on language
 */
function formatDayDisplay(date) {
  const lang = getCurrentLanguage();
  return date.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    weekday: "short"
  });
}

/**
 * Generate business days (Mon-Fri) for a given month
 */
function getBusinessDaysInMonth(year, month) {
  const businessDays = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();

    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      businessDays.push(date);
    }
  }

  return businessDays;
}

/**
 * Generate 15-minute interval bookings for a single day
 */
function generateDayBookings(date, baseUtilization, hourlyPattern) {
  const bookings = [];
  const startHour = 8;
  const endHour = 19;

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute of [0, 15, 30, 45]) {
      const time = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;

      // Find hourly pattern rate
      const hourPattern = hourlyPattern.find(
        (h) => h.hour === `${hour.toString().padStart(2, "0")}:00`
      );
      const hourRate = hourPattern ? hourPattern.rate : baseUtilization;

      // Add small variation per 15-min interval
      const utilizationRate = Math.min(
        100,
        Math.max(10, hourRate + (Math.random() - 0.5) * 8)
      );

      bookings.push({
        time,
        timestamp: new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          hour,
          minute
        ),
        utilizationRate: Math.round(utilizationRate * 10) / 10,
        bookedSlots: Math.round((utilizationRate / 100) * 18) // 18 slots per interval
      });
    }
  }

  return bookings;
}

/**
 * Generate energy usage for 15-minute intervals
 */
function generateEnergyIntervals(date, dailyEnergyTotal, bookings) {
  const intervals = [];

  // Calculate energy distribution based on booking utilization
  const totalUtilization = bookings.reduce(
    (sum, b) => sum + b.utilizationRate,
    0
  );

  bookings.forEach((booking) => {
    const intervalEnergy =
      (dailyEnergyTotal * booking.utilizationRate) / totalUtilization;

    intervals.push({
      time: booking.time,
      timestamp: booking.timestamp,
      energyKwh: Math.round(intervalEnergy * 10) / 10,
      cost: Math.round(intervalEnergy * 0.28 * 100) / 100,
      spaceBreakdown: {
        meetingRooms: Math.round(intervalEnergy * 0.42 * 10) / 10,
        conferenceRooms: Math.round(intervalEnergy * 0.28 * 10) / 10,
        openOffice: Math.round(intervalEnergy * 0.2 * 10) / 10,
        breakoutAreas: Math.round(intervalEnergy * 0.1 * 10) / 10
      }
    });
  });

  return intervals;
}

/**
 * Generate booking duration data for intervals
 */
function generateDurationIntervals(bookings, avgDuration) {
  return bookings.map((booking) => {
    // Vary duration based on time of day
    const hour = parseInt(booking.time.split(":")[0]);
    let durationMultiplier = 1.0;

    // Longer meetings in morning and afternoon
    if (hour >= 9 && hour <= 11) durationMultiplier = 1.2;
    else if (hour >= 14 && hour <= 16) durationMultiplier = 1.15;
    else if (hour >= 17) durationMultiplier = 0.8;

    const intervalDuration =
      Math.round(
        (avgDuration * durationMultiplier + (Math.random() - 0.5) * 0.5) * 10
      ) / 10;
    const efficiency = Math.round((75 + Math.random() * 20) * 10) / 10;

    return {
      time: booking.time,
      timestamp: booking.timestamp,
      avgDuration: intervalDuration,
      efficiency: efficiency,
      activeBookings: Math.round(booking.bookedSlots * 0.8) // 80% are active
    };
  });
}

/**
 * Generate CO2 awareness data for intervals
 */
function generateCO2Intervals(bookings, dailyEmissions) {
  const totalUtilization = bookings.reduce(
    (sum, b) => sum + b.utilizationRate,
    0
  );

  return bookings.map((booking) => {
    const intervalEmissions =
      (dailyEmissions * booking.utilizationRate) / totalUtilization;
    const awarenessScore = Math.round((60 + Math.random() * 25) * 10) / 10;

    return {
      time: booking.time,
      timestamp: booking.timestamp,
      // ✅ CHANGE THIS LINE - ensure minimum 0.5 kg
      emissions: Math.max(0.5, Math.round(intervalEmissions * 10) / 10),
      awarenessScore: awarenessScore,
      positiveActions: Math.floor(Math.random() * 3)
    };
  });
}

/**
 * Generate daily distribution factors
 */
function generateDailyDistributionFactors(numDays) {
  const factors = [];
  let sum = 0;

  for (let i = 0; i < numDays; i++) {
    const dayOfWeek = i % 5;
    const weekdayWeight = 1 + Math.sin((dayOfWeek * Math.PI) / 4) * 0.1;
    const variation = 0.9 + Math.random() * 0.2;
    const factor = weekdayWeight * variation;

    factors.push(factor);
    sum += factor;
  }

  return factors.map((f) => f / sum);
}

/**
 * Generate detailed daily data for a month
 */
function generateDetailedMonthData(monthData) {
  const [year, month] = monthData.month.split("-").map(Number);
  const businessDays = getBusinessDaysInMonth(year, month - 1);
  const numDays = businessDays.length;

  // Generate distribution factors
  const dailyEnergyFactors = generateDailyDistributionFactors(numDays);
  const dailyBookingFactors = generateDailyDistributionFactors(numDays);

  const dailyData = businessDays.map((date, dayIndex) => {
    // Distribute monthly totals across days
    const dailyEnergyTotal = Math.round(
      monthData.energyPerBooking.totalEnergy * dailyEnergyFactors[dayIndex]
    );
    const dailyBookingsTotal = Math.round(
      monthData.bookingDuration.totalBookings * dailyBookingFactors[dayIndex]
    );

    const dailyCost = Math.round(dailyEnergyTotal * 0.28 * 100) / 100;
    const dailyEmissions = Math.max(100, Math.round(dailyEnergyTotal * 0.3)); // CO2 factor - minimum 100 kg per day

    // Small variations for daily metrics
    const dailyUtilization =
      monthData.bookingUtilization.rate + (Math.random() - 0.5) * 5;
    const dailyComfort =
      monthData.comfortSatisfaction.overallScore + (Math.random() - 0.5) * 3;
    const dailyDuration =
      monthData.bookingDuration.avgDuration + (Math.random() - 0.5) * 0.3;
    const dailyCO2Score =
      monthData.co2Awareness.overallScore + (Math.random() - 0.5) * 4;

    // Generate 44 bookings per day (11 hours × 4 intervals)
    const bookings = generateDayBookings(
      date,
      dailyUtilization,
      monthData.bookingUtilization.hourlyUtilization
    );

    return {
      date: date.toISOString().split("T")[0],
      displayDate: formatDayDisplay(date),
      dayOfWeek: date.toLocaleDateString("en-US", { weekday: "long" }),
      month: monthData.month,
      year: year,

      // 44 bookings per day (11 hours × 4 intervals)
      bookings: bookings,

      // Energy intervals
      energyIntervals: generateEnergyIntervals(
        date,
        dailyEnergyTotal,
        bookings
      ),

      // Duration intervals
      durationIntervals: generateDurationIntervals(bookings, dailyDuration),

      // CO2 intervals
      co2Intervals: generateCO2Intervals(bookings, dailyEmissions),

      // Daily summary with ALL KPIs
      dailySummary: {
        // Booking Utilization KPIs
        bookingUtilization: {
          rate: Math.round(dailyUtilization * 10) / 10,
          bookedSlots: Math.round((dailyUtilization / 100) * 800),
          availableSlots: 800,
          topSpaces: monthData.bookingUtilization.topSpaces
        },

        // Energy Per Booking KPIs
        energyPerBooking: {
          avgEnergy: monthData.energyPerBooking.avgEnergy,
          totalEnergy: dailyEnergyTotal,
          totalCost: dailyCost,
          spaceBreakdown: monthData.energyPerBooking.spaceBreakdown,
          floorEnergyDistribution:
            monthData.energyPerBooking.floorEnergyDistribution
        },

        // Comfort Satisfaction KPIs
        comfortSatisfaction: {
          overallScore: Math.round(dailyComfort * 10) / 10,
          energyAnalytics: Math.round(dailyComfort * 0.92 * 10) / 10,
          dynamicOccupancy: Math.round(dailyComfort * 0.94 * 10) / 10,
          utilizationAnalytics: Math.round(dailyComfort * 0.96 * 10) / 10
        },

        // Booking Duration KPIs
        bookingDuration: {
          avgDuration: Math.round(dailyDuration * 10) / 10,
          totalBookings: dailyBookingsTotal,
          efficiency: Math.round((80 + Math.random() * 15) * 10) / 10,
          roomEfficiency: monthData.bookingDuration.roomEfficiency
        },

        // CO2 Awareness KPIs
        co2Awareness: {
          overallScore: Math.round(dailyCO2Score * 10) / 10,
          emissions: dailyEmissions,
          energyEfficiency: Math.round(dailyCO2Score * 0.93 * 10) / 10,
          wasteDiversion: Math.round(dailyCO2Score * 0.68 * 10) / 10,
          waterConservation: Math.round(dailyCO2Score * 1.08 * 10) / 10
        }
      }
    };
  });

  return {
    month: monthData.month,
    displayMonth: monthData.displayMonth,
    year: year,
    businessDaysCount: businessDays.length,
    totalDataPoints: businessDays.length * 44,
    dailyData,

    // Monthly summary (from original data)
    monthlySummary: {
      bookingUtilization: monthData.bookingUtilization,
      energyPerBooking: monthData.energyPerBooking,
      comfortSatisfaction: monthData.comfortSatisfaction,
      bookingDuration: monthData.bookingDuration,
      co2Awareness: monthData.co2Awareness
    }
  };
}

/**
 * Get detailed data for all months or specific month
 */
export function getDetailedRentersTenantData(monthString = null) {
  // Get the generated dashboard data (with all months up to today)
  const dashboardData = getRentersTenantsDashboardData();

  if (monthString) {
    const monthData = dashboardData.monthlyData.find(
      (m) => m.month === monthString
    );
    if (!monthData) return null;
    return generateDetailedMonthData(monthData);
  }

  return dashboardData.monthlyData.map((monthData) =>
    generateDetailedMonthData(monthData)
  );
}

/**
 * Get all bookings across all months
 */
export function getAllRentersBookings() {
  const allMonthsDetailed = getDetailedRentersTenantData();

  const allBookings = [];
  allMonthsDetailed.forEach((month) => {
    month.dailyData.forEach((day) => {
      day.bookings.forEach((booking) => {
        allBookings.push({
          ...booking,
          date: day.date,
          dayOfWeek: day.dayOfWeek,
          month: month.month,
          year: month.year
        });
      });
    });
  });

  return allBookings;
}

/**
 * Get statistics
 */
export function getDetailedRentersDataStats() {
  const allMonthsDetailed = getDetailedRentersTenantData();

  const totalDays = allMonthsDetailed.reduce(
    (sum, m) => sum + m.businessDaysCount,
    0
  );
  const totalDataPoints = allMonthsDetailed.reduce(
    (sum, m) => sum + m.totalDataPoints,
    0
  );

  return {
    totalMonths: allMonthsDetailed.length,
    totalBusinessDays: totalDays,
    totalBookingIntervals: totalDataPoints,
    intervalsPerDay: 44,
    intervalDuration: "15 minutes",
    operatingHours: "08:00 - 19:00",
    dateRange: {
      start: allMonthsDetailed[0]?.month,
      end: allMonthsDetailed[allMonthsDetailed.length - 1]?.month
    }
  };
}

/**
 * Filter data by year, month, and/or day
 */
export function filterDetailedRentersData(filters = {}) {
  const { year, month, day } = filters;
  let allData = getDetailedRentersTenantData();

  // Filter by year
  if (year) {
    allData = allData.filter((m) => m.year === parseInt(year));
  }

  // Filter by month
  if (month) {
    allData = allData.filter((m) => m.month === month);
  }

  // Flatten to daily data
  let dailyData = [];
  allData.forEach((monthData) => {
    dailyData.push(...monthData.dailyData);
  });

  // Filter by specific day
  if (day) {
    dailyData = dailyData.filter((d) => d.date === day);
  }

  return dailyData;
}

/**
 * Get available years
 */
export function getAvailableRentersYears() {
  const allData = getDetailedRentersTenantData();
  const years = [...new Set(allData.map((m) => m.year))];
  return years.sort();
}

/**
 * Get available months for a year
 */
export function getAvailableRentersMonths(year = null) {
  let allData = getDetailedRentersTenantData();

  if (year) {
    allData = allData.filter((m) => m.year === parseInt(year));
  }

  return allData.map((m) => ({
    value: m.month,
    text: m.displayMonth,
    year: m.year
  }));
}

/**
 * Get available days for a month
 */
export function getAvailableRentersDays(monthString) {
  const monthData = getDetailedRentersTenantData(monthString);

  if (!monthData) return [];

  return monthData.dailyData.map((d) => ({
    value: d.date,
    text: d.displayDate,
    dayOfWeek: d.dayOfWeek
  }));
}
